<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleClose"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              {{ isEditing ? '编辑敏感词' : '添加敏感词' }}
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              {{ isEditing ? '修改敏感词信息和设置' : '添加新的敏感词到词库' }}
            </p>
          </div>
          <button
            @click="handleClose"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 添加/编辑结果显示 -->
        <div v-if="success" class="mb-6">
          <div class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="flex items-center">
              <CheckCircleIcon class="h-5 w-5 text-green-600 mr-2" />
              <h4 class="text-sm font-medium text-green-900">{{ isEditing ? '更新成功' : '添加成功' }}</h4>
            </div>
            <div class="mt-2 text-sm text-green-700">
              {{ successMessage }}
            </div>
          </div>
        </div>

        <div v-if="error" class="mb-6">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-center">
              <ExclamationTriangleIcon class="h-5 w-5 text-red-600 mr-2" />
              <h4 class="text-sm font-medium text-red-900">{{ isEditing ? '更新失败' : '添加失败' }}</h4>
            </div>
            <div class="mt-2 text-sm text-red-700">
              {{ errorMessage }}
            </div>
          </div>
        </div>

        <!-- 表单内容 -->
        <div v-if="!success">
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- 敏感词内容 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                敏感词内容 <span class="text-red-500">*</span>
              </label>
              <input
                v-model="formData.word"
                type="text"
                required
                placeholder="输入敏感词内容"
                class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                :disabled="loading"
              />
              <p class="mt-1 text-xs text-gray-500">
                请输入需要检测的敏感词，长度1-200个字符
              </p>
            </div>

            <!-- 所属分类 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                所属分类 <span class="text-red-500">*</span>
              </label>
              <SearchableSelect
                v-model="formData.category"
                :options="categoryOptions"
                placeholder="请选择分类"
                label-key="label"
                value-key="value"
                :disabled="loading"
                class="w-full"
              />
              <p class="mt-1 text-xs text-gray-500">
                选择敏感词所属的分类，不同分类有不同的处理策略
              </p>
            </div>

            <!-- 匹配方式 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                匹配方式 <span class="text-red-500">*</span>
              </label>
              <select
                v-model="formData.match_type"
                required
                class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                :disabled="loading"
              >
                <option value="contains">包含匹配</option>
                <option value="exact">精确匹配</option>
                <option value="regex">正则匹配</option>
              </select>
              <div class="mt-1 text-xs text-gray-500">
                <div class="space-y-1">
                  <div><strong>包含匹配</strong>：文本中包含该词即触发（推荐）</div>
                  <div><strong>精确匹配</strong>：必须是完整单词才触发</div>
                  <div><strong>正则匹配</strong>：使用正则表达式匹配（高级用法）</div>
                </div>
              </div>
            </div>

            <!-- 替换建议 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                替换建议
              </label>
              <textarea
                v-model="formData.alternative_words"
                placeholder="输入替换建议，多个建议用逗号分隔，如：友好,和谐,积极"
                rows="3"
                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                :disabled="loading"
              />
              <p class="mt-1 text-xs text-gray-500">
                为用户提供替换建议，多个建议用逗号分隔，最多10个，每个长度不超过100字符
              </p>
            </div>

            <!-- 状态设置 -->
            <div class="flex items-center">
              <input
                v-model="formData.is_active"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                :disabled="loading"
              />
              <label class="ml-2 block text-sm text-gray-700">启用敏感词检测</label>
            </div>

            <!-- 预览区域 -->
            <div v-if="formData.word" class="bg-gray-50 rounded-lg p-4">
              <h4 class="text-sm font-medium text-gray-900 mb-2">敏感词预览</h4>
              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <span class="text-sm text-gray-600">敏感词:</span>
                  <code class="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-mono">{{ formData.word }}</code>
                </div>
                <div v-if="selectedCategory" class="flex items-center space-x-2">
                  <span class="text-sm text-gray-600">分类:</span>
                  <span 
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="getSeverityBadgeClass(selectedCategory.severity_level)"
                  >
                    {{ selectedCategory.name }} - {{ getSeverityLabel(selectedCategory.severity_level) }}
                  </span>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-sm text-gray-600">匹配方式:</span>
                  <span class="text-sm text-gray-900">{{ getMatchTypeLabel(formData.match_type) }}</span>
                </div>
                <div v-if="alternativeWordsList.length > 0" class="flex items-start space-x-2">
                  <span class="text-sm text-gray-600 mt-0.5">替换建议:</span>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="alt in alternativeWordsList"
                      :key="alt"
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {{ alt }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- 底部按钮 -->
        <div class="flex items-center justify-end space-x-3 mt-6">
          <button
            @click="handleClose"
            :disabled="loading"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {{ success ? '完成' : '取消' }}
          </button>
          <button
            v-if="!success"
            @click="handleSubmit"
            :disabled="!isFormValid || loading"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
          >
            <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ loading ? (isEditing ? '更新中...' : '添加中...') : (isEditing ? '更新敏感词' : '添加敏感词') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'
import { sensitiveWordService, sensitiveWordCategoryService } from '@/services/sensitiveWordService'
import SearchableSelect from '@/components/common/SearchableSelect.vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  word: {
    type: Object,
    default: null
  },
  categories: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()

// 响应式数据
const loading = ref(false)
const success = ref(false)
const error = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const formData = ref({
  word: '',
  category: '',
  match_type: 'contains',
  alternative_words: '',
  is_active: true
})

// 计算属性
const isEditing = computed(() => !!(props.word?.id))

const isFormValid = computed(() => {
  return formData.value.word.trim() && 
         formData.value.category && 
         formData.value.match_type
})

const categoryOptions = computed(() => {
  return props.categories.map(category => ({
    value: category.id,
    label: `${category.name} - ${getSeverityLabel(category.severity_level)}`
  }))
})

const selectedCategory = computed(() => {
  return props.categories.find(cat => cat.id == formData.value.category)
})

const alternativeWordsList = computed(() => {
  if (!formData.value.alternative_words) return []
  return formData.value.alternative_words.split(',')
    .map(word => word.trim())
    .filter(word => word.length > 0)
})

// 方法
const getSeverityLabel = (severity) => {
  const labels = {
    'low': '低风险',
    'medium': '中风险',
    'high': '高风险',
    'forbidden': '禁止使用'
  }
  return labels[severity] || severity
}

const getSeverityBadgeClass = (severity) => {
  const classes = {
    'low': 'bg-blue-100 text-blue-800',
    'medium': 'bg-yellow-100 text-yellow-800',
    'high': 'bg-orange-100 text-orange-800',
    'forbidden': 'bg-red-100 text-red-800'
  }
  return classes[severity] || 'bg-gray-100 text-gray-800'
}

const getMatchTypeLabel = (matchType) => {
  const labels = {
    'exact': '精确匹配',
    'contains': '包含匹配',
    'regex': '正则匹配'
  }
  return labels[matchType] || matchType
}

const resetForm = () => {
  formData.value = {
    word: '',
    category: '',
    match_type: 'contains',
    alternative_words: '',
    is_active: true
  }
}

const fillForm = (word) => {
  formData.value = {
    word: word.word || '',
    category: word.category || '',
    match_type: word.match_type || 'contains',
    alternative_words: word.alternative_words_list ? word.alternative_words_list.join(', ') : '',
    is_active: word.is_active !== false
  }
}

const handleSubmit = async () => {
  if (!isFormValid.value) return

  loading.value = true
  error.value = false
  
  try {
    const wordData = {
      word: formData.value.word.trim(),
      category: formData.value.category,
      match_type: formData.value.match_type,
      alternative_words: formData.value.alternative_words.trim(),
      is_active: formData.value.is_active
    }

    let result
    if (isEditing.value) {
      result = await sensitiveWordService.updateSensitiveWord(props.word.id, wordData)
    } else {
      result = await sensitiveWordService.createSensitiveWord(wordData)
    }

    // 成功时直接处理返回的数据
    success.value = true
    successMessage.value = `敏感词"${wordData.word}"${isEditing.value ? '更新' : '添加'}成功！`
    
    toast.success(successMessage.value)
    
    setTimeout(() => {
      emit('success', result)
      handleClose()
    }, 1500)
  } catch (error) {
    console.error(`${isEditing.value ? '更新' : '添加'}敏感词失败:`, error)
    error.value = true
    errorMessage.value = `${isEditing.value ? '更新' : '添加'}敏感词失败，请稍后重试`
    toast.error(errorMessage.value)
  } finally {
    loading.value = false
  }
}

const getFieldLabel = (field) => {
  const labels = {
    'word': '敏感词内容',
    'category': '所属分类',
    'match_type': '匹配方式',
    'alternative_words': '替换建议'
  }
  return labels[field] || field
}

const handleClose = () => {
  // 重置所有状态
  loading.value = false
  success.value = false
  error.value = false
  resetForm()
  
  emit('close')
}

// 监听弹窗显示/隐藏
watch(() => props.show, (newValue) => {
  if (newValue) {
    // 弹窗打开时
    loading.value = false
    success.value = false
    error.value = false
    
    if (props.word) {
      fillForm(props.word)
    } else {
      resetForm()
    }
  }
})

// 监听编辑的敏感词变化
watch(() => props.word, (newWord) => {
  if (props.show && newWord) {
    fillForm(newWord)
  }
}, { deep: true })
</script>
